// Pure, UI-agnostic helpers for the Internal UW Notes panel (#35208). Kept in a plain .js
// file so they can be unit-tested (staff-fe .jsx component tests don't run under vitest in a
// worktree — see AGENTS.md / the react jsx-dev-runtime artifact).

/** A note is shown in the panel unless it's been soft-deleted. */
export const isActive = (note) => !note?.deleted;

/** Newest first, by createdAt. Returns a copy — never mutates the input. */
export const sortByNewest = (notes = []) =>
  [...notes].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

/** Split active notes into sticky (pinned) and internal, each newest-first. */
export const partitionNotes = (notes = []) => {
  const active = notes.filter(isActive);
  return {
    sticky: sortByNewest(active.filter((note) => note.sticky)),
    internal: sortByNewest(active.filter((note) => !note.sticky))
  };
};

/**
 * Record-identifying variables for the notes query/mutations. policyId wins when both are
 * present (post-bind source of truth), mirroring the resolver's `resolveTarget`.
 */
export const recordVars = ({ offerId, policyId } = {}) => {
  if (policyId) {
    return { policyId };
  }
  if (offerId) {
    return { offerId };
  }
  return {};
};

/**
 * Internal UW notes are for internal staff, where "internal" means "not an agency user" — the
 * same rule internal documents use. Deliberately NOT a permission group: carrier (Forreston/CFM)
 * staff get their Cognito groups from Descope roles and never held CanViewInternalTasks, which hid
 * the panel from them entirely (#35208 reopen). Fails closed until the session has resolved the flag.
 */
export const canViewInternalUwNotes = (session) => session?.isAgency === false;

/** Writing notes additionally excludes ViewOnly staff, matching the schema's mutation groups. */
export const canEditInternalUwNotes = (session) => canViewInternalUwNotes(session) && !session.viewOnly;

/** Whether we have any record to load notes for. */
export const hasRecord = (vars) => Boolean(vars?.offerId || vars?.policyId);

/** Pull the notes array out of the getInternalUwNotes union result. */
export const notesFromResult = (data) => data?.getInternalUwNotes?.notes ?? [];

/**
 * A GraphQL union member is an error when it's the error type or reports success:false.
 * The backend catches business failures (e.g. "internal note not found") and returns a
 * normal `InternalUwNotesError` response, so Apollo's onError never fires — callers must
 * inspect the union here before treating a mutation/query as successful. See #35208.
 */
export const isErrorUnion = (result) =>
  !!result && (result.__typename === 'InternalUwNotesError' || result.success === false);

/** Human message for an error union member, falling back to a caller-supplied default. */
export const unionErrorMessage = (result, fallback) => (result && result.error) || fallback;
